import useArtistPopularTracks, { RankedArtistTrack } from "@hooks/useArtistPopularTracks";
import useUserPlaylists from "@hooks/usePlayList";
import useSpotifyTracks from "@hooks/useSpotifyTracks";
import FavoriteIcon from "@mui/icons-material/Favorite";
import NonFavoriteIcon from "@mui/icons-material/FavoriteBorder";
import { Button, IconButton, Tooltip } from "@mui/material";
import { styled, useTheme } from "@mui/system";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { CurrentlyPlayingState, PlayingStates } from "src/types/currentlyPlayingState";
import { formatMilliseconds } from "src/utils";

const PAGE_SIZE = 10;

interface ArtistPageLocationState {
  fromAlbumId?: string;
}

interface ArtistPageProps {
  currentlyPlayingSong: CurrentlyPlayingState;
}

interface RankedTrackView {
  track: RankedArtistTrack;
  globalRank: number;
}

const ArtistPageContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  width: "100%"
});

const ArtistHeader = styled("div")({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  marginBottom: "14px",
  maxWidth: "100%",
  "& h1": {
    fontSize: "clamp(1.55rem, 4vw, 2rem)",
    lineHeight: 1.15,
    margin: "0 0 12px",
    overflowWrap: "anywhere",
    textAlign: "center"
  }
});

const ArtistImage = styled("img")({
  width: "124px",
  height: "124px",
  borderRadius: "50%",
  objectFit: "cover",
  marginBottom: "12px"
});

const ArtistTable = styled("table")(({ theme }) => ({
  width: "100%",
  borderCollapse: "collapse",
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  tableLayout: "fixed",
  "& .optional-column": {
    [theme.breakpoints.down("sm")]: {
      display: "none"
    }
  }
}));

const ArtistTableWrapper = styled("div")({
  width: "100%",
  overflowX: "auto",
  marginBottom: "20px"
});

const TableHeader = styled("th")(({ theme }) => ({
  padding: "4px 6px",
  textAlign: "left",
  backgroundColor: theme.palette.background.default,
  color: theme.palette.text.secondary,
  fontSize: "0.8rem",
  position: "sticky",
  top: 0,
  zIndex: 1
}));

const TableCell = styled("td")(({ theme }) => ({
  padding: "3px 6px",
  textAlign: "left",
  borderBottom: `1px solid ${theme.palette.divider}`,
  fontSize: "0.86rem"
}));

const TableRow = styled("tr")(({ theme }) => ({
  cursor: "pointer",
  height: "52px",
  "&:hover": {
    backgroundColor: theme.palette.action.hover
  }
}));

const TitleCell = styled(TableCell)({
  overflowWrap: "anywhere",
  width: "auto"
});

const SmallCell = styled(TableCell)({
  width: "5%",
  minWidth: "40px",
  textAlign: "center",
  padding: "2px"
});

const ActionCell = styled(SmallCell)({
  minWidth: "48px"
});

const SongTitleButton = styled("button")(({ theme }) => ({
  appearance: "none",
  border: 0,
  background: "transparent",
  color: theme.palette.primary.main,
  cursor: "pointer",
  font: "inherit",
  fontWeight: 600,
  padding: 0,
  textAlign: "left",
  width: "100%",
  overflowWrap: "anywhere",
  "&:hover": {
    textDecoration: "underline"
  }
}));

const AlbumCell = styled(TableCell)(({ theme }) => ({
  width: "30%",
  cursor: "pointer",
  color: theme.palette.primary.main,
  "&:hover": {
    textDecoration: "underline"
  }
}));

const PaginationControls = styled("div")({
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: "12px",
  justifyContent: "center",
  marginBottom: "12px",
  textAlign: "center"
});

const ArtistPage: React.FunctionComponent<ArtistPageProps> = ({ currentlyPlayingSong }) => {
  const theme = useTheme();
  const { artistId } = useParams<{ artistId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { switchTrack } = useSpotifyTracks();
  const { toggleLikedSongs } = useUserPlaylists();
  const { artist, tracks, loading, loadingMore, hasMore, loadMore, error } =
    useArtistPopularTracks(artistId);
  const [page, setPage] = useState(0);
  const [likedTrackIds, setLikedTrackIds] = useState<Set<string>>(new Set());
  const [likingTrackIds, setLikingTrackIds] = useState<Set<string>>(new Set());
  const [hideLikedSongs, setHideLikedSongs] = useState(false);
  const [requestedPage, setRequestedPage] = useState<number | null>(null);
  const state = location.state as ArtistPageLocationState | null;
  const currentlyPlayingTrack =
    currentlyPlayingSong.state === PlayingStates.Playing ||
    currentlyPlayingSong.state === PlayingStates.Paused
      ? currentlyPlayingSong.currentlyPlayingObject.item
      : null;
  const currentArtistId = currentlyPlayingTrack?.artists?.[0]?.id;
  const currentAlbumId = currentlyPlayingTrack?.album?.id;
  const currentTrackId = currentlyPlayingTrack?.id;
  const fromAlbumId = currentAlbumId ?? state?.fromAlbumId;

  useEffect(() => {
    setPage(0);
    setLikedTrackIds(new Set());
    setHideLikedSongs(false);
    setRequestedPage(null);
  }, [artistId]);

  useEffect(() => {
    if (!currentTrackId || !currentArtistId || currentArtistId === artistId) return;

    navigate(`/artist/${currentArtistId}`, {
      replace: true,
      state: { fromAlbumId: currentAlbumId }
    });
  }, [artistId, currentAlbumId, currentArtistId, currentTrackId, navigate]);

  useEffect(() => {
    setLikedTrackIds(prev => {
      const next = new Set(prev);
      tracks.forEach(track => {
        if (track.liked) {
          next.add(track.id);
        }
      });
      return next;
    });
  }, [tracks]);

  const rankedTracks = useMemo<RankedTrackView[]>(
    () => tracks.map((track, index) => ({ track, globalRank: index + 1 })),
    [tracks]
  );
  const visibleTracks = useMemo(
    () =>
      hideLikedSongs
        ? rankedTracks.filter(({ track }) => !likedTrackIds.has(track.id))
        : rankedTracks,
    [hideLikedSongs, likedTrackIds, rankedTracks]
  );
  const pageCount = Math.max(1, Math.ceil(visibleTracks.length / PAGE_SIZE));
  const pagedTracks = useMemo(
    () => visibleTracks.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE),
    [page, visibleTracks]
  );
  const needsMoreTracksForNextPage = visibleTracks.length <= (page + 1) * PAGE_SIZE;
  const canGoNext = page < pageCount - 1 || hasMore;

  useEffect(() => {
    setPage(0);
    setRequestedPage(null);
  }, [hideLikedSongs]);

  useEffect(() => {
    if (page > pageCount - 1) {
      setPage(pageCount - 1);
    }
  }, [page, pageCount]);

  useEffect(() => {
    if (requestedPage === null) return;

    if (visibleTracks.length > requestedPage * PAGE_SIZE || !hasMore) {
      setPage(requestedPage);
      setRequestedPage(null);
    }
  }, [hasMore, requestedPage, visibleTracks.length]);

  async function goToNextPage(): Promise<void> {
    const nextPage = page + 1;

    if (needsMoreTracksForNextPage && hasMore) {
      setRequestedPage(nextPage);
      await loadMore();
      return;
    }

    setPage(nextPage);
  }

  function playSong(trackID: string, event?: React.MouseEvent): void {
    event?.stopPropagation();
    switchTrack(trackID);
    navigate("/");
  }

  function navigateToAlbum(event: React.MouseEvent<HTMLTableCellElement>, albumId: string): void {
    event.stopPropagation();
    navigate(`/album/${albumId}`);
  }

  async function addToLikedSongs(
    event: React.MouseEvent<HTMLButtonElement>,
    trackId: string,
    trackUri: string
  ): Promise<void> {
    event.stopPropagation();
    if (likingTrackIds.has(trackId)) return;

    setLikingTrackIds(prev => new Set(prev).add(trackId));
    try {
      await toggleLikedSongs(trackUri);
      setLikedTrackIds(prev => new Set(prev).add(trackId));
    } catch (err) {
      console.error("Failed to add track to liked songs", err);
    } finally {
      setLikingTrackIds(prev => {
        const next = new Set(prev);
        next.delete(trackId);
        return next;
      });
    }
  }

  if (loading || (artistId && !artist && !error)) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;

  return (
    <ArtistPageContainer>
      {fromAlbumId && (
        <Button
          variant="outlined"
          onClick={() => navigate(`/album/${fromAlbumId}`)}
          sx={{ mb: 2, alignSelf: "flex-start" }}
        >
          Back to album
        </Button>
      )}

      <ArtistHeader>
        {artist?.images?.[0]?.url && <ArtistImage src={artist.images[0].url} alt={artist.name} />}
        <h1>{artist?.name ?? "Artist"}</h1>
      </ArtistHeader>

      {tracks.length > 0 && (
        <PaginationControls>
          <Button
            variant={hideLikedSongs ? "contained" : "outlined"}
            onClick={() => setHideLikedSongs(prev => !prev)}
          >
            Hide liked
          </Button>
          <Button variant="outlined" disabled={page === 0} onClick={() => setPage(page - 1)}>
            Previous
          </Button>
          <span>
            Page {page + 1} · {visibleTracks.length} shown · {tracks.length} loaded
          </span>
          <Button variant="outlined" disabled={loadingMore || !canGoNext} onClick={goToNextPage}>
            {loadingMore ? "Loading..." : "Next"}
          </Button>
        </PaginationControls>
      )}

      <ArtistTableWrapper>
        <ArtistTable theme={theme}>
          <thead>
            <tr>
              <TableHeader theme={theme} style={{ width: "5%" }}></TableHeader>
              <TableHeader theme={theme} style={{ width: "5%" }}>
                #
              </TableHeader>
              <TableHeader theme={theme}>Title</TableHeader>
              <TableHeader className="optional-column" theme={theme}>
                Album
              </TableHeader>
              <TableHeader className="optional-column" theme={theme} style={{ width: "12%" }}>
                Popularity
              </TableHeader>
              <TableHeader className="optional-column" theme={theme} style={{ width: "12%" }}>
                Duration
              </TableHeader>
            </tr>
          </thead>
          <tbody>
            {pagedTracks.map(({ track, globalRank }) => (
              <TableRow key={track.id} theme={theme} onClick={() => playSong(track.id)}>
                <ActionCell theme={theme}>
                  <Tooltip title="Add to liked songs" placement="top">
                    <IconButton
                      aria-label={`Add ${track.name} to liked songs`}
                      color={likedTrackIds.has(track.id) ? "primary" : "default"}
                      disabled={likingTrackIds.has(track.id)}
                      onClick={event => addToLikedSongs(event, track.id, track.uri)}
                      size="small"
                    >
                      {likedTrackIds.has(track.id) ? <FavoriteIcon /> : <NonFavoriteIcon />}
                    </IconButton>
                  </Tooltip>
                </ActionCell>
                <SmallCell theme={theme}>{globalRank}</SmallCell>
                <TitleCell theme={theme}>
                  <SongTitleButton
                    theme={theme}
                    type="button"
                    onClick={event => playSong(track.id, event)}
                  >
                    {track.name}
                  </SongTitleButton>
                </TitleCell>
                <AlbumCell
                  className="optional-column"
                  theme={theme}
                  onClick={event => navigateToAlbum(event, track.album.id)}
                >
                  {track.album.name}
                </AlbumCell>
                <SmallCell className="optional-column" theme={theme}>
                  {track.popularity}
                </SmallCell>
                <SmallCell className="optional-column" theme={theme}>
                  {formatMilliseconds(track.duration_ms)} min
                </SmallCell>
              </TableRow>
            ))}
          </tbody>
        </ArtistTable>
      </ArtistTableWrapper>

      {tracks.length === 0 ? <div>No songs found.</div> : null}
      {tracks.length > 0 && visibleTracks.length === 0 ? <div>No unliked songs found.</div> : null}
    </ArtistPageContainer>
  );
};

export default ArtistPage;
